import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';

export interface SmallSelectOption {
  value: string;
  label: string;
}

interface SmallSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SmallSelectOption[];
}

export function SmallSelect({ value, onChange, options }: SmallSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [portalMounted, setPortalMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const selected = options.find(o => o.value === value);

  useEffect(() => { setPortalMounted(true); }, []);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="flex items-center gap-1 pl-2 pr-1.5 py-1 rounded-md border border-border bg-transparent text-xs font-medium text-text-secondary hover:border-primary hover:text-text-primary transition-colors focus:outline-none"
      >
        <span>{selected?.label}</span>
        <ChevronDown size={10} className={`text-text-tertiary transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {portalMounted && createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, y: -4, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.97 }}
              transition={{ duration: 0.12 }}
              className="fixed bg-background-card border border-border rounded-lg shadow-lg overflow-hidden z-[9999] py-1"
              style={{ top: menuPos.top, left: menuPos.left, minWidth: menuPos.width }}
            >
              {options.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-1.5 text-xs text-text-primary hover:bg-background-surface transition-colors"
                >
                  <span>{opt.label}</span>
                  {opt.value === value && <Check size={10} className="text-primary flex-shrink-0" />}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
